// Static marketing content for the Astria landing page.
//
// Seeded from the approved landing design (AstriaFinal/public/index.html) and
// maintained here by hand. English strings double as translation keys (see
// landingTranslations.ts), so editing one means updating its row there too.

export type LandingLangCode =
  | "en"
  | "th"
  | "vi"
  | "id"
  | "ms"
  | "fil"
  | "ko"
  | "ja"
  | "zh-CN"
  | "hi"
  | "ar"
  | "es"
  | "pt-BR"
  | "fr"
  | "de"
  | "ta"
  | "mr"
  | "it"
  | "ru";

export interface LandingLanguage {
  code: LandingLangCode;
  nativeName: string;
  englishName: string;
  dir: "ltr" | "rtl";
  /** Pricing region preselected when this language is chosen. */
  defaultRegion: string | null;
}

export interface RegionPrice {
  id: string;
  country: string;
  currency: string;
  monthly: string;
  yearly: string;
  /** Yearly price expressed per month, e.g. "≈ $10.00". */
  perMonth: string;
  /** Percentage saved by paying yearly. */
  save: number;
}

export interface TitledItem {
  title: string;
  text?: string;
}

export interface CtaContent {
  label: string;
  action: LandingAction;
}

export type PlanId = "free" | "pro" | "global";

export interface PlanTier {
  id: PlanId;
  kicker?: string;
  isPro?: boolean;
  badge?: string;
  name: string;
  tagline?: string;
  headline: string;
  features: string[];
  cta: CtaContent;
}

export interface ProfileField {
  label: string;
  type: string;
  required: boolean;
  placeholder?: string;
}

export type LandingAction =
  | "start_map"
  | "start_free"
  | "start_pro"
  | "start_global"
  | "see_plans"
  | "start_energy_match"
  | "learn_more"
  | "see_blueprint"
  | "try_life_gps"
  | "write_letter"
  | "letter_trial";

/** Cards in the "What brings you here today?" grid; also the analytics `card` value. */
export type ExploreCardId =
  | "relationships"
  | "energy_match"
  | "letter_never_sent"
  | "life_direction"
  | "life_blueprint"
  | "global_wisdom";

/** Section anchor ids on the page. */
export type SectionId =
  | "energy_match"
  | "letter_never_sent"
  | "life_gps"
  | "profile"
  | "global_wisdom";

export const DEFAULT_LANGUAGE: LandingLangCode = "en";
export const DEFAULT_PRICING_REGION = "US";

export const LANGUAGES: readonly LandingLanguage[] = [
  {
    code: "en",
    nativeName: "English",
    englishName: "English",
    dir: "ltr",
    defaultRegion: "US",
  },
  {
    code: "th",
    nativeName: "ไทย",
    englishName: "Thai",
    dir: "ltr",
    defaultRegion: "TH",
  },
  {
    code: "vi",
    nativeName: "Tiếng Việt",
    englishName: "Vietnamese",
    dir: "ltr",
    defaultRegion: "VN",
  },
  {
    code: "id",
    nativeName: "Bahasa Indonesia",
    englishName: "Indonesian",
    dir: "ltr",
    defaultRegion: "ID",
  },
  {
    code: "ms",
    nativeName: "Bahasa Melayu",
    englishName: "Malay",
    dir: "ltr",
    defaultRegion: "MY",
  },
  {
    code: "fil",
    nativeName: "Filipino",
    englishName: "Filipino",
    dir: "ltr",
    defaultRegion: "PH",
  },
  {
    code: "ko",
    nativeName: "한국어",
    englishName: "Korean",
    dir: "ltr",
    defaultRegion: "KR",
  },
  {
    code: "ja",
    nativeName: "日本語",
    englishName: "Japanese",
    dir: "ltr",
    defaultRegion: "JP",
  },
  {
    code: "zh-CN",
    nativeName: "中文",
    englishName: "Chinese (Simplified)",
    dir: "ltr",
    defaultRegion: "SG",
  },
  {
    code: "hi",
    nativeName: "हिन्दी",
    englishName: "Hindi",
    dir: "ltr",
    defaultRegion: "IN",
  },
  {
    code: "ar",
    nativeName: "العربية",
    englishName: "Arabic",
    dir: "rtl",
    defaultRegion: "GCC",
  },
  {
    code: "es",
    nativeName: "Español",
    englishName: "Spanish",
    dir: "ltr",
    defaultRegion: "ES",
  },
  {
    code: "pt-BR",
    nativeName: "Português",
    englishName: "Portuguese",
    dir: "ltr",
    defaultRegion: "BR",
  },
  {
    code: "fr",
    nativeName: "Français",
    englishName: "French",
    dir: "ltr",
    defaultRegion: null,
  },
  {
    code: "de",
    nativeName: "Deutsch",
    englishName: "German",
    dir: "ltr",
    defaultRegion: null,
  },
  {
    code: "ta",
    nativeName: "தமிழ்",
    englishName: "Tamil",
    dir: "ltr",
    defaultRegion: "IN",
  },
  {
    code: "mr",
    nativeName: "मराठी",
    englishName: "Marathi",
    dir: "ltr",
    defaultRegion: "IN",
  },
  {
    code: "it",
    nativeName: "Italiano",
    englishName: "Italian",
    dir: "ltr",
    defaultRegion: null,
  },
  {
    code: "ru",
    nativeName: "Русский",
    englishName: "Russian",
    dir: "ltr",
    defaultRegion: null,
  },
];

/** Display prices per country. Marketing copy only — checkout prices come from the billing backend. */
// Prices mirror the checkout: monthly = the client's Master Flow v10 list, yearly =
// utils/regions.ts annualPrice (the same numbers seeded into the database plans).
// ES maps to the "spanish" region. AR, CO, CL and PE have no purchasable plan and
// keep their old display prices: confirm with the client whether to keep them.
export const REGION_PRICES: readonly RegionPrice[] = [
  {
    id: "US",
    country: "United States",
    currency: "USD",
    monthly: "$7.99",
    yearly: "$79.99",
    perMonth: "≈ $6.67",
    save: 17,
  },
  {
    id: "UK",
    country: "United Kingdom",
    currency: "GBP",
    monthly: "£7.99",
    yearly: "£79.99",
    perMonth: "≈ £6.67",
    save: 17,
  },
  {
    id: "CA",
    country: "Canada",
    currency: "CAD",
    monthly: "C$6.99",
    yearly: "C$69.99",
    perMonth: "≈ C$5.83",
    save: 17,
  },
  {
    id: "TH",
    country: "Thailand",
    currency: "THB",
    monthly: "฿149",
    yearly: "฿1,490",
    perMonth: "≈ ฿124",
    save: 17,
  },
  {
    id: "VN",
    country: "Vietnam",
    currency: "VND",
    monthly: "99.000₫",
    yearly: "990.000₫",
    perMonth: "≈ 82.500₫",
    save: 17,
  },
  {
    id: "ID",
    country: "Indonesia",
    currency: "IDR",
    monthly: "Rp 39.000",
    yearly: "Rp 390.000",
    perMonth: "≈ Rp 32.500",
    save: 17,
  },
  {
    id: "PH",
    country: "Philippines",
    currency: "PHP",
    monthly: "₱149",
    yearly: "₱1,490",
    perMonth: "≈ ₱124",
    save: 17,
  },
  {
    id: "MY",
    country: "Malaysia",
    currency: "MYR",
    monthly: "RM12",
    yearly: "RM120",
    perMonth: "≈ RM10.00",
    save: 17,
  },
  {
    id: "SG",
    country: "Singapore",
    currency: "SGD",
    monthly: "S$4.99",
    yearly: "S$49.99",
    perMonth: "≈ S$4.17",
    save: 17,
  },
  {
    id: "KR",
    country: "Korea",
    currency: "KRW",
    monthly: "₩4,900",
    yearly: "₩49,000",
    perMonth: "≈ ₩4,083",
    save: 17,
  },
  {
    id: "JP",
    country: "Japan",
    currency: "JPY",
    monthly: "¥450",
    yearly: "¥4,500",
    perMonth: "≈ ¥375",
    save: 17,
  },
  {
    id: "IN",
    country: "India",
    currency: "INR",
    monthly: "₹149",
    yearly: "₹1,490",
    perMonth: "≈ ₹124",
    save: 17,
  },
  {
    id: "GCC",
    country: "GCC (USD)",
    currency: "USD",
    monthly: "$5.99",
    yearly: "$59.99",
    perMonth: "≈ $5.00",
    save: 17,
  },
  {
    id: "ES",
    country: "Spain",
    currency: "EUR",
    monthly: "4,99 €",
    yearly: "49,99 €",
    perMonth: "≈ 4,17 €",
    save: 17,
  },
  {
    id: "BR",
    country: "Brazil",
    currency: "BRL",
    monthly: "R$ 14",
    yearly: "R$ 140",
    perMonth: "≈ R$ 11,67",
    save: 17,
  },
  {
    id: "MX",
    country: "Mexico",
    currency: "MXN",
    monthly: "MXN $79",
    yearly: "MXN $790",
    perMonth: "≈ MXN $66",
    save: 17,
  },
  {
    id: "AR",
    country: "Argentina",
    currency: "ARS",
    monthly: "ARS $6.900",
    yearly: "ARS $56.900",
    perMonth: "≈ ARS $4.742",
    save: 31,
  },
  {
    id: "CO",
    country: "Colombia",
    currency: "COP",
    monthly: "COP $24.900",
    yearly: "COP $199.900",
    perMonth: "≈ COP $16.658",
    save: 33,
  },
  {
    id: "CL",
    country: "Chile",
    currency: "CLP",
    monthly: "CLP $6.900",
    yearly: "CLP $56.900",
    perMonth: "≈ CLP $4.742",
    save: 31,
  },
  {
    id: "PE",
    country: "Peru",
    currency: "PEN",
    monthly: "S/24",
    yearly: "S/199",
    perMonth: "≈ S/16.58",
    save: 31,
  },
];

export const HERO = {
  eyebrow: "One Astria · Your language, your culture, your story",
  headline: "Astria — Your Personal Intelligence Companion",
  subheadline: "Calm guidance. Soft mystery. Always by your side.",
  body: "Astrology for reflection. Life GPS for your next step.",
  ctaPrimary: {
    label: "Meet your Astria",
    action: "start_map",
  },
  ctaSecondary: {
    label: "How it works",
    action: "learn_more",
  },
} as const;

export const UNIQUE_MAP = {
  eyebrow: "Inside Astria",
  body: "A few calm places, one companion that remembers what matters to you.",
  items: [
    {
      title: "Today",
      text: "What matters today, why it matters for you, and what to do next.",
    },
    {
      title: "Life GPS",
      text: "How to move through your current phase — not how well you are performing.",
    },
    {
      title: "Astrology",
      text: "A plain-language reflection of your sky, deeper whenever you want.",
    },
    {
      title: "Ask Astria",
      text: "Ask anything. Concise by default, deeper whenever you want.",
    },
    {
      title: "Journey",
      text: "Your personal timeline of chapters, decisions and moments.",
    },
  ],
};
export const CALM_GUIDANCE = {
  moments: [
    {
      label: "☀ Morning brief",
      text: "A 30-second read of today's rhythm before the day begins.",
    },
    {
      label: "✦ Timing nudge",
      text: "A quiet heads-up when a good window opens.",
    },
    {
      label: "☾ Evening reflection",
      text: "One question to close the day with clarity.",
    },
  ],
};
export const LIFE_GPS = {
  eyebrow: "Life GPS",
  headline: "Your Current Phase",
  sentence: "A calm direction for today.",
  states: [
    { label: "Direction", sampleValue: "Forward", fill: 0.78 },
    { label: "Pace", sampleValue: "Easing", fill: 0.58 },
    { label: "Energy", sampleValue: "Warm", fill: 0.68 },
    { label: "Focus", sampleValue: "Rising", fill: 0.46 },
  ],
} as const;

export const LIFE_AREAS = {
  eyebrow: "Life Areas",
  body: "Each area is shown as a calm state — never a score to chase.",
  groups: [
    {
      label: "Foundations",
      sampleValue: "Steady",
      description:
        "The ground you stand on: health, home, money and daily rhythm.",
    },
    {
      label: "Expansion",
      sampleValue: "Balanced",
      description: "Where you are growing: work, learning and new directions.",
    },
    {
      label: "Expression",
      sampleValue: "Strong",
      description:
        "How you connect and create: relationships and self-expression.",
    },
  ],
} as const;

export type CollectionAccess = "preview" | "open" | "locked";

export const accessFor = (plan: PlanId, isHome: boolean): CollectionAccess => {
  if (plan === "global") return "open";
  if (!isHome) return "locked";
  return plan === "pro" ? "open" : "preview";
};

/** Paywall shown when a free visitor reaches the daily limit. */
export const LIMIT_LOCK = {
  caption: "When you reach your daily limit",
  title: "Continue Your Journey",
  text: "Your free guidance session is complete. Unlock deeper conversations, personalized insights and your complete Life Blueprint.",
  cta: { label: "Unlock Astria Pro", action: "see_plans" },
} as const;

export const STEPS_TO_CLARITY = {
  eyebrow: "How it works",
  steps: [
    {
      number: "01",
      title: "Tell Astria about you",
      text: "Share when and where you were born.",
    },
    {
      number: "02",
      title: "See your Life GPS",
      text: "Understand the phase you are in right now.",
    },
    {
      number: "03",
      title: "Take one next step",
      text: "Small, practical and connected to your life.",
    },
    {
      number: "04",
      title: "Astria learns with you",
      text: "Guidance grows more personal, with you in control.",
    },
  ],
};
export const PROFILE: {
  eyebrow: string;
  body: string;
  fields: ProfileField[];
  cta: CtaContent;
  successMessage: string;
  note: string;
} = {
  eyebrow: "Start here",
  body: "Four details are all it takes. Your map is private, and you can edit it anytime.",
  fields: [
    {
      label: "Name",
      type: "text",
      required: true,
      placeholder: "Your first name",
    },
    {
      label: "Date of birth",
      type: "date",
      required: true,
    },
    {
      label: "Time of birth",
      type: "time",
      required: false,
    },
    {
      label: "Location",
      type: "text",
      required: true,
      placeholder: "City of birth",
    },
  ],
  cta: {
    label: "Create profile",
    action: "start_map",
  },
  successMessage: "✓ Profile created — your map is on its way",
  note: "No birth time? That's fine — you can add it later.",
};
export const PRIVACY = {
  eyebrow: "Trust",
  items: [
    {
      title: "Private by default",
      text: "Birth data is encrypted and never sold. Export or delete it anytime.",
    },
    {
      title: "Personalization, explained",
      text: "Every answer shows which parts of your context it used.",
    },
    {
      title: "You control your memory",
      text: "See, correct, forget or pause what Astria knows — it still helps either way.",
    },
    {
      title: "No fear, no fake certainty",
      text: "Guidance that leaves the choice with you. It never claims to know the future or asks you to depend on it.",
    },
  ],
};

export type PlanCurrency = "USD" | "THB";
export type BillingCycle = "monthly" | "yearly";

interface CyclePrices {
  monthly: number;
  yearly: number;
}

/** Display prices. Marketing copy only: checkout prices come from the billing backend. */
export const PLAN_PRICES: Readonly<
  Record<
    PlanCurrency,
    { pro: CyclePrices; global: CyclePrices; energyMatch: number; energyMatchPro: number }
  >
> = {
  USD: {
    pro: { monthly: 7.99, yearly: 69 },
    global: { monthly: 14.99, yearly: 119 },
    energyMatch: 3.99,
    energyMatchPro: 2.99,
  },
  THB: {
    pro: { monthly: 249, yearly: 1990 },
    global: { monthly: 399, yearly: 3490 },
    energyMatch: 129,
    energyMatchPro: 99,
  },
};

export const PLANS: {
  eyebrow: string;
  headline: string;
  body: string;
  fineprint: string;
  trialNote: string;
  globalPass: string;
  tiers: PlanTier[];
  energyMatch: { headline: string; text: string; perMatch: string; cta: CtaContent; included: string };
} = {
  eyebrow: "Membership",
  headline: "Choose the journey that's <em>right for you.</em>",
  body: "Whether you're just getting started or ready to explore more deeply, Astria grows with you.",
  fineprint: "Language is always free · Cancel anytime",
  /** Shown on the Pro card: the trial is free, needs no card and never converts on its own. */
  trialNote: "7 days free · no card needed · no automatic charge",
  globalPass: "Free Global Pass: unlock one country collection for 24 hours every month.",
  tiers: [
    {
      id: "free",
      name: "Astria Free",
      headline: "Begin your journey",
      features: [
        "Daily Guidance",
        "Daily Flow",
        "Life Blueprint preview",
        "Relationship preview",
        "Compatibility preview",
        "15 messages per day",
        "3-day chat history",
        "1 Global Pass per month, valid for 24 hours",
      ],
      cta: { label: "Explore Free", action: "start_free" },
    },
    {
      id: "pro",
      badge: "Most Popular",
      name: "Astria Pro",
      headline: "Know yourself more deeply",
      features: [
        "Unlimited chat",
        "Unlimited chat history",
        "Full Life Blueprint",
        "Full Compatibility",
        "Relationship Insights",
        "Life Direction",
        "Letter Never Sent",
        "PDF reports",
        "Full home-country collection",
        "2 Global Passes per month, 24 hours each",
      ],
      cta: { label: "Unlock Astria Pro", action: "start_pro" },
    },
    {
      id: "global",
      badge: "Best Value",
      name: "Astria Global",
      headline: "Wisdom from around the world",
      features: [
        "Everything in Pro",
        "All 21 country collections",
        "Unlimited global access, no passes required",
        "Energy Match™ Included",
        "Future country collections included",
        "China BaZi",
        "France Big 3",
        "Germany Career Direction",
        "Italy Relationship Snapshot",
        "Russia Destiny Themes",
      ],
      cta: { label: "Unlock Astria Global", action: "start_global" },
    },
  ],
  energyMatch: {
    headline: "Some connections change everything.",
    text: "Who are you looking for in life?",
    perMatch: "per match",
    included: "Included with Astria Global.",
    cta: { label: "Unlock Energy Match™", action: "start_energy_match" },
  },
};

export const FAQ = {
  eyebrow: "Questions",
  items: [
    {
      q: "Does Astria predict the future?",
      a: "No. Astria offers perspective and timing, never fear-based predictions or fake certainty. You stay in charge of your next step.",
    },
    {
      q: "What does Astria remember about me?",
      a: "Only what helps. You can view, correct, forget or pause your memory anytime, and export or delete your data.",
    },
    {
      q: "What if I don't know my birth time?",
      a: "You can still use Astria. Some details will be broader, and you can add your time whenever you find it.",
    },
    {
      q: "Can I use Astria in my language?",
      a: "Yes. Astria works in 19 languages and adapts its tone and terms to where you live.",
    },
    {
      q: "What is the difference between Pro and Global?",
      a: "Pro unlocks your complete personal experience. Global adds country-specific wisdom collections including BaZi, Big 3, Career Direction and other premium cultural systems.",
    },
    {
      q: "Can I purchase Energy Match™ separately?",
      a: "Yes. Energy Match™ can be purchased individually or accessed as part of Astria Global.",
    },
    {
      q: "Can I cancel anytime?",
      a: "Always. Cancel in a tap and keep access until the end of your billing period.",
    },
  ],
};
export const FINAL_CTA = {
  eyebrow: "Your Astria",
  cta: {
    label: "Get started",
    action: "start_map",
  },
};
export const FOOTER_NOTE: string =
  "© 2026 Astria · For reflection and self-discovery, not professional advice.";
